// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { MouseEvent, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { WarningOutlined } from "@ant-design/icons";
import {
  mdiContentCopy,
  mdiDelete,
  mdiDotsVertical,
  mdiFolderMove,
  mdiPencil
} from "@mdi/js";
import { Checkbox, Input, Popconfirm, Popover, Select, Tooltip, Typography } from "antd";
import styled from "styled-components";

import { ToastType, showToast } from "components/shared/Toast";

import { setCheckedPadScenarios } from "../../store/features";
import {
  ActionListWrapper,
  ShapefileAction
} from "../project/layers/components/layer-managers/shared";
import { useProjectListQuery } from "../project/projects/queries";
import { IconButton } from "./ScenarioConfigurationTable";
import { usePadScenario } from "./contexts/usePadScenario";
import useCloneScenario from "./hooks/useCloneScenario";
import useDeleteScenario from "./hooks/useDeleteScenario";
import useMoveScenarioToProject from "./hooks/useMoveScenarioToProject";
import { usePadScenarioPermissions } from "./hooks/usePadScenarioPermissions";
import { Scenario } from "./models/scenario";

interface ScenarioItemProps {
  item: Scenario;
  onSelect: (item: Scenario) => void;
  onRequestEdit: () => void;
}

// Defined at module scope (not inside ScenarioList) so its identity is stable
// across parent re-renders — otherwise a mutation in the parent would remount
// every row and wipe local state like the clone input and inline error.
export default function ScenarioItem({
  item,
  onSelect,
  onRequestEdit
}: ScenarioItemProps) {
  const { state, dispatch: scenarioDispatch } = usePadScenario();
  const dispatch = useDispatch();
  const { mutateAsync: cloneScenario } = useCloneScenario();
  const { mutateAsync: deleteScenario } = useDeleteScenario();
  const { mutateAsync: moveScenario } = useMoveScenarioToProject();
  const { data: projects } = useProjectListQuery();
  const { showFullPadScenario, isStickManagementMode } = usePadScenarioPermissions();

  const [moveToProjectId, setMoveToProjectId] = useState("");
  const [newScenarioId, setNewScenarioId] = useState("");
  const [cloneError, setCloneError] = useState("");
  const [clonePopconfirmOpen, setClonePopconfirmOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Vetoes the single auto-close antd fires after a failed clone, so the
  // popconfirm stays open with the inline error instead of dismissing.
  const keepCloneOpenRef = useRef(false);
  const [moveError, setMoveError] = useState("");
  const [movePopconfirmOpen, setMovePopconfirmOpen] = useState(false);
  const keepMoveOpenRef = useRef(false);

  const isChecked = state.checkedScenarios.some((s) => s.scenarioId === item.scenarioId);

  const handleCheckboxChange = (e: MouseEvent) => {
    e.stopPropagation();
    const next = isChecked
      ? state.checkedScenarios.filter((s) => s.scenarioId !== item.scenarioId)
      : [...state.checkedScenarios, item];
    scenarioDispatch({ type: "SET_CHECKED_SCENARIOS", payload: next });
    dispatch(
      setCheckedPadScenarios(
        next.map((s) => ({
          // Use _id.$oid (MongoDB ObjectId) as the scenarioId — this is what the
          // tile server and filter API use to identify scenarios (not the display label).
          scenarioId: s._id.$oid,
          showSticksOnMap: true,
          play: s.play
        }))
      )
    );
  };

  const handleCloneScenario = async () => {
    if (!state.scenario.scenarioId) {
      return;
    }
    setCloneError("");
    try {
      await cloneScenario({
        scenarioId: state.scenario._id.$oid,
        newScenarioId: newScenarioId
      });
      // Success: let antd's auto-close dismiss the popconfirm.
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        "Failed to clone scenario";
      // Show the error inline and veto the auto-close so the popconfirm stays open.
      keepCloneOpenRef.current = true;
      setCloneError(message);
    }
  };
  const handleDelete = async (item: Scenario) => {
    if (!item.scenarioId) {
      return;
    }
    try {
      await deleteScenario(item._id);
      scenarioDispatch({
        type: "SET_SCENARIO",
        payload: {
          scenarioId: ""
        } as Scenario
      });
    } catch (err) {
      showToast("scenario-container", "Failed to delete scenario", ToastType.Error);
    }
  };
  const handleMoveScenario = async () => {
    try {
      await moveScenario({
        scenarioId: state.scenario._id,
        projectId: moveToProjectId
      });
      scenarioDispatch({
        type: "SET_SCENARIO",
        payload: {
          scenarioId: ""
        } as Scenario
      });
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        "Failed to move scenario";
      keepMoveOpenRef.current = true;
      setMoveError(message);
    }
  };
  return (
    <ItemWrapper
      key={item.scenarioId}
      onClick={() => onSelect(item)}
      selected={item.scenarioId == state.scenario.scenarioId}>
      <Checkbox
        checked={isChecked}
        onClick={handleCheckboxChange}
        style={{ marginRight: 8 }}
      />
      <ScenarioLabel
        title={`${item.scenarioId}${item.padName ? ` - ${item.padName}` : ""}`}>
        {item.scenarioId}
        {item.padName ? ` - ${item.padName}` : ""}
      </ScenarioLabel>
      {item.allSticksHaveDepth === false && (
        <Tooltip title="One or more sticks are missing depth (Z) values">
          <WarningOutlined
            style={{ color: "#faad14", fontSize: 14, flexShrink: 0, marginRight: 4 }}
          />
        </Tooltip>
      )}

      {(showFullPadScenario || isStickManagementMode) && (
        <ButtonGroup>
          <Popover
            overlayClassName={"popover-no-padding"}
            placement={"bottom"}
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            content={
              <ActionListWrapper style={{ minWidth: "130px" }}>
                <Popconfirm
                  open={clonePopconfirmOpen}
                  onOpenChange={(open) => {
                    // Veto the auto-close that follows a failed clone (set in
                    // handleCloneScenario) so the inline error stays visible.
                    if (!open && keepCloneOpenRef.current) {
                      keepCloneOpenRef.current = false;
                      return;
                    }
                    setClonePopconfirmOpen(open);
                    if (!open) {
                      setNewScenarioId("");
                      setCloneError("");
                    }
                  }}
                  title={
                    <div style={{ width: 250 }}>
                      <Input
                        autoFocus
                        status={cloneError ? "error" : undefined}
                        onFocus={(e) => e.target.select()}
                        placeholder={"New Scenario Id"}
                        value={newScenarioId}
                        onChange={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setNewScenarioId(e.target.value);
                          if (cloneError) setCloneError("");
                        }}
                      />
                      {cloneError && (
                        <Typography.Text
                          type="danger"
                          style={{
                            display: "block",
                            marginTop: 4,
                            fontSize: 12,
                            whiteSpace: "normal",
                            wordBreak: "break-word"
                          }}>
                          {cloneError}
                        </Typography.Text>
                      )}
                    </div>
                  }
                  okText={"Clone"}
                  onConfirm={handleCloneScenario}>
                  <ShapefileAction onClick={() => setPopoverOpen(false)}>
                    <Icon path={mdiContentCopy} size={1} />
                    Clone
                  </ShapefileAction>
                </Popconfirm>
                <Popconfirm
                  open={movePopconfirmOpen}
                  onOpenChange={(open) => {
                    if (!open && keepMoveOpenRef.current) {
                      keepMoveOpenRef.current = false;
                      return;
                    }
                    setMovePopconfirmOpen(open);
                    if (!open) {
                      setMoveError("");
                    }
                  }}
                  okText={"Move"}
                  onConfirm={handleMoveScenario}
                  title={
                    <div style={{ width: 250 }}>
                      <Select
                        value={moveToProjectId}
                        onChange={(value) => {
                          setMoveToProjectId(value);
                          if (moveError) setMoveError("");
                        }}
                        status={moveError ? "error" : undefined}
                        placeholder={"Select Project"}
                        style={{ width: "200px" }}
                        options={(projects ?? []).map((p) => ({
                          label: p.projectName,
                          value: p.projectId
                        }))}
                      />
                      {moveError && (
                        <Typography.Text
                          type="danger"
                          style={{
                            display: "block",
                            marginTop: 4,
                            fontSize: 12,
                            whiteSpace: "normal",
                            wordBreak: "break-word"
                          }}>
                          {moveError}
                        </Typography.Text>
                      )}
                    </div>
                  }>
                  <ShapefileAction onClick={() => setPopoverOpen(false)}>
                    <Icon path={mdiFolderMove} size={1} />
                    Move
                  </ShapefileAction>
                </Popconfirm>
                <ShapefileAction onClick={onRequestEdit}>
                  <Icon path={mdiPencil} size={1} />
                  Edit
                </ShapefileAction>
                <Popconfirm
                  placement="bottom"
                  onConfirm={async (evt) => {
                    evt.stopPropagation();
                    evt.preventDefault();
                    await handleDelete(item);
                  }}
                  okText="Delete"
                  okType="danger"
                  title={`Are you sure you want to delete scenario ${item.scenarioId}?`}>
                  <ShapefileAction danger onClick={() => setPopoverOpen(false)}>
                    <Icon path={mdiDelete} size={1} />
                    Delete
                  </ShapefileAction>
                </Popconfirm>
              </ActionListWrapper>
            }
            trigger={"click"}>
            <IconButton
              type="text"
              icon={<Icon path={mdiDotsVertical} size={1} />}
              title="Edit"
            />
          </Popover>
        </ButtonGroup>
      )}
    </ItemWrapper>
  );
}

const ScenarioLabel = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;
const ButtonGroup = styled.div`
  display: flex;
  margin: auto;
  gap: 5px;
`;
const ItemWrapper = styled.div`
  cursor: pointer;
  min-width: 120px;
  height: 45px;
  padding: 5px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => (props.selected ? "#f0f0f0" : "white")};
  transition: box-shadow 0.3s ease-in-out;
  z-index: 1;

  &:hover {
    box-shadow: 0 0 10px rgba(159, 169, 179, 0.5);
    z-index: 10; /* Ensure the hovered item is above others */
  }
`;
